import React, { useState } from 'react';
import { Brain, Calendar, CheckCircle, Clock, TrendingUp, Globe, FileText, MessageSquare, Target, Database, Zap, ArrowRight, PlayCircle, AlertCircle, Info } from 'lucide-react';

export default function AIAgentWorkflowPage() {
  const [activeSection, setActiveSection] = useState<'overview' | 'workflow' | 'actions' | 'schedule'>('overview');

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Brain className="text-purple-600" size={40} />
          <h1 className="text-3xl font-bold text-gray-900">Autonomous AI Marketing Agent</h1>
        </div>
        <p className="text-gray-600 text-lg">
          Your 24/7 AI-powered marketing assistant that monitors, analyzes, and optimizes your digital presence automatically
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 mb-8 border-b border-gray-200">
        {[
          { id: 'overview', label: 'Overview', icon: Info },
          { id: 'workflow', label: 'Step-by-Step Workflow', icon: PlayCircle },
          { id: 'actions', label: 'Autonomous Actions', icon: Zap },
          { id: 'schedule', label: 'Schedule & Status', icon: Calendar }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id as any)}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
                activeSection === tab.id
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          {/* What It Does */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-8 border border-purple-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Brain className="text-purple-600" size={28} />
              What Does the AI Agent Do?
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              The AI Marketing Agent is your autonomous digital marketing manager. It runs every night at 2:00 AM, 
              analyzing your entire marketing ecosystem, identifying opportunities, and taking action to improve your 
              online presence—all while you sleep.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <TrendingUp className="text-green-600 mb-2" size={24} />
                <h3 className="font-bold text-gray-900 mb-1">Monitors Performance</h3>
                <p className="text-sm text-gray-600">
                  Tracks rankings, reviews, website health, and competitor activity across all platforms
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <Target className="text-blue-600 mb-2" size={24} />
                <h3 className="font-bold text-gray-900 mb-1">Identifies Opportunities</h3>
                <p className="text-sm text-gray-600">
                  Uses AI to spot ranking improvements, content gaps, and optimization opportunities
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <Zap className="text-yellow-600 mb-2" size={24} />
                <h3 className="font-bold text-gray-900 mb-1">Takes Autonomous Action</h3>
                <p className="text-sm text-gray-600">
                  Automatically optimizes SEO, generates content, and updates your marketing materials
                </p>
              </div>
            </div>
          </div>

          {/* Key Benefits */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Why This Matters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-semibold text-gray-900">24/7 Optimization</h4>
                  <p className="text-sm text-gray-600">Never miss a ranking opportunity or competitor move</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-semibold text-gray-900">Consistent Execution</h4>
                  <p className="text-sm text-gray-600">No human errors, no forgotten tasks, perfect consistency</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-semibold text-gray-900">Data-Driven Decisions</h4>
                  <p className="text-sm text-gray-600">Every action based on real performance data and AI analysis</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-semibold text-gray-900">Cost Effective</h4>
                  <p className="text-sm text-gray-600">Replaces hours of manual work, 7 days a week</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step-by-Step Workflow */}
      {activeSection === 'workflow' && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Complete Daily Workflow</h2>
            <p className="text-gray-600 mb-4">
              Every night at 2:00 AM EST, the AI agent executes this comprehensive workflow:
            </p>
          </div>

          {/* Phase 1: Data Collection */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Database size={24} />
                Phase 1: Intelligence Gathering (2:00 AM - 2:45 AM)
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {[
                {
                  step: '1.1',
                  title: 'Website Health Check',
                  description: 'Scans all 3 company websites (Hales AC, CCS, Abraham AC) for:',
                  details: [
                    'Core Web Vitals (LCP, FID, CLS scores)',
                    'Page load times and performance metrics',
                    'Broken links and 404 errors',
                    'SSL certificate status',
                    'Mobile responsiveness issues'
                  ],
                  icon: Globe,
                  color: 'blue'
                },
                {
                  step: '1.2',
                  title: 'SEO Rankings Update',
                  description: 'Tracks search engine positions for all target keywords:',
                  details: [
                    '50+ HVAC keywords per county (Pinellas, Hillsborough, Palm Beach, Broward)',
                    'Desktop and mobile rankings separately',
                    'Local Pack positions (Google Maps 3-pack)',
                    'Position changes vs yesterday',
                    'Competitor ranking movements'
                  ],
                  icon: TrendingUp,
                  color: 'green'
                },
                {
                  step: '1.3',
                  title: 'Google Business Profile Monitoring',
                  description: 'Collects GBP data for all locations:',
                  details: [
                    'Review count and average rating',
                    'New reviews (response needed)',
                    'Post views and engagement',
                    'Photo upload activity',
                    'Q&A section updates',
                    'Competitor GBP activity'
                  ],
                  icon: MessageSquare,
                  color: 'purple'
                },
                {
                  step: '1.4',
                  title: 'Search Console Analysis',
                  description: 'Pulls Google Search Console data:',
                  details: [
                    'Top 100 queries by impressions',
                    'CTR for each query',
                    'Average position changes',
                    'Click volume trends',
                    'Cannibalization detection (multiple pages ranking for same query)'
                  ],
                  icon: Target,
                  color: 'orange'
                }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`bg-${item.color}-100 rounded-lg p-2`}>
                        <Icon className={`text-${item.color}-600`} size={24} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="bg-gray-800 text-white text-xs font-bold px-2 py-1 rounded">
                            STEP {item.step}
                          </span>
                          <h4 className="text-lg font-bold text-gray-900">{item.title}</h4>
                        </div>
                        <p className="text-gray-700 mb-2">{item.description}</p>
                        <ul className="space-y-1">
                          {item.details.map((detail, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <ArrowRight size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Phase 2: AI Analysis */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Brain size={24} />
                Phase 2: AI Intelligence Analysis (2:45 AM - 3:15 AM)
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {[
                {
                  step: '2.1',
                  title: 'Opportunity Detection',
                  description: 'AI analyzes collected data to identify:',
                  details: [
                    'Keywords you rank 11-20 (page 2) → Quick wins to push to page 1',
                    'High impression, low CTR queries → Meta description optimization needed',
                    'Competitor ranking gains → Investigate their content changes',
                    'Declining rankings → Immediate content refresh required',
                    'New keyword opportunities → Emerging search trends'
                  ]
                },
                {
                  step: '2.2',
                  title: 'Content Gap Analysis',
                  description: 'Identifies missing content opportunities:',
                  details: [
                    'Queries competitors rank for but you don\'t',
                    'Seasonal content opportunities (AC summer demand, heating winter demand)',
                    'Questions from "People Also Ask" boxes',
                    'Local service area pages missing',
                    'Blog topics with high search volume'
                  ]
                },
                {
                  step: '2.3',
                  title: 'Performance Scoring',
                  description: 'Calculates key performance metrics:',
                  details: [
                    'Overall SEO health score (0-100)',
                    'Visibility score by county',
                    'Competitive position index',
                    'Review velocity and sentiment',
                    'Website technical health score'
                  ]
                },
                {
                  step: '2.4',
                  title: 'Action Prioritization',
                  description: 'AI prioritizes actions by impact:',
                  details: [
                    'Critical issues (site down, major ranking drops) → Immediate alerts',
                    'High-impact opportunities (easy page 1 rankings) → Execute today',
                    'Medium-impact tasks (content updates) → Schedule this week',
                    'Low-priority items (minor optimizations) → Batch for next month',
                    'Long-term strategic items → Add to quarterly roadmap'
                  ]
                }
              ].map((item, idx) => (
                <div key={idx} className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded">
                          STEP {item.step}
                        </span>
                        <h4 className="text-lg font-bold text-gray-900">{item.title}</h4>
                      </div>
                      <p className="text-gray-700 mb-2">{item.description}</p>
                      <ul className="space-y-1">
                        {item.details.map((detail, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <ArrowRight size={16} className="text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Phase 3: Autonomous Actions */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Zap size={24} />
                Phase 3: Automated Execution (3:15 AM - 4:00 AM)
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {[
                {
                  step: '3.1',
                  title: 'WordPress SEO Updates',
                  description: 'Automatically optimizes your WordPress sites:',
                  details: [
                    'Updates page titles with better keywords',
                    'Rewrites meta descriptions for higher CTR',
                    'Optimizes H1/H2 headings for target keywords',
                    'Adds alt text to images for SEO',
                    'Updates internal linking structure',
                    'Generates XML sitemaps'
                  ],
                  autoLevel: 'Full Auto'
                },
                {
                  step: '3.2',
                  title: 'Blog Content Generation',
                  description: 'Creates and publishes SEO-optimized blog posts:',
                  details: [
                    'Generates 2-3 blog posts per week per site',
                    'Targets identified keyword opportunities',
                    'Follows Florida HVAC content rules (seasonal, local)',
                    'Includes images with proper SEO tags',
                    'Optimized for featured snippets',
                    'Auto-publishes to WordPress'
                  ],
                  autoLevel: 'Full Auto'
                },
                {
                  step: '3.3',
                  title: 'Review Response Generation',
                  description: 'Drafts personalized review responses:',
                  details: [
                    'Generates custom responses for each new review',
                    'Addresses specific issues mentioned',
                    'Thanks customers and reinforces brand values',
                    'Includes call-to-action for future service',
                    'Maintains consistent brand voice',
                    'REQUIRES HUMAN APPROVAL before posting'
                  ],
                  autoLevel: 'Draft Only'
                },
                {
                  step: '3.4',
                  title: 'GMB Post Creation',
                  description: 'Creates Google Business Profile posts:',
                  details: [
                    'Generates offer posts (seasonal specials)',
                    'Creates update posts (company news)',
                    'Produces educational content posts',
                    'Includes relevant images and CTAs',
                    'Optimized posting schedule',
                    'REQUIRES HUMAN APPROVAL before publishing'
                  ],
                  autoLevel: 'Draft Only'
                }
              ].map((item, idx) => (
                <div key={idx} className="border border-green-200 rounded-lg p-4 bg-green-50">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">
                          STEP {item.step}
                        </span>
                        <h4 className="text-lg font-bold text-gray-900">{item.title}</h4>
                        <span className={`ml-auto text-xs font-bold px-3 py-1 rounded-full ${
                          item.autoLevel === 'Full Auto'
                            ? 'bg-green-600 text-white'
                            : 'bg-yellow-500 text-white'
                        }`}>
                          {item.autoLevel}
                        </span>
                      </div>
                      <p className="text-gray-700 mb-2">{item.description}</p>
                      <ul className="space-y-1">
                        {item.details.map((detail, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <ArrowRight size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Phase 4: Reporting */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 p-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <FileText size={24} />
                Phase 4: Reporting & Alerts (4:00 AM - 4:15 AM)
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded">STEP 4.1</span>
                  Daily Summary Email
                </h4>
                <p className="text-gray-700 mb-2">Sends comprehensive report to stakeholders:</p>
                <ul className="space-y-1">
                  {[
                    'Executive summary of key metrics',
                    'Ranking changes (gains and losses)',
                    'New reviews and sentiment',
                    'Actions taken by AI agent',
                    'Items requiring human approval',
                    'Week-over-week performance trends'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <ArrowRight size={16} className="text-orange-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded">STEP 4.2</span>
                  Critical Alerts
                </h4>
                <p className="text-gray-700 mb-2">Immediate notifications for urgent issues:</p>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="bg-white rounded p-3 border-l-4 border-red-500">
                    <div className="font-semibold text-red-600 text-sm mb-1">🚨 CRITICAL</div>
                    <div className="text-xs text-gray-600">Website down, major ranking drops, negative reviews</div>
                  </div>
                  <div className="bg-white rounded p-3 border-l-4 border-yellow-500">
                    <div className="font-semibold text-yellow-600 text-sm mb-1">⚠️ WARNING</div>
                    <div className="text-xs text-gray-600">Slow page speed, declining CTR, competitor gains</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Autonomous Actions Detail */}
      {activeSection === 'actions' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">What Actions Can the AI Take Autonomously?</h2>
            <p className="text-gray-600 mb-6">
              The AI agent operates on different automation levels depending on the action's impact and risk:
            </p>

            {/* Full Automation */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold">
                  FULL AUTOMATION - No Human Review Required
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    title: 'SEO Meta Updates',
                    description: 'Updates title tags, meta descriptions, and heading structures to improve rankings and CTR',
                    frequency: 'Daily',
                    impact: 'High'
                  },
                  {
                    title: 'Blog Content Publishing',
                    description: 'Generates and publishes SEO-optimized blog posts following brand guidelines',
                    frequency: '2-3x per week',
                    impact: 'High'
                  },
                  {
                    title: 'Image SEO Optimization',
                    description: 'Adds alt text, optimizes file names, and compresses images for better performance',
                    frequency: 'Continuous',
                    impact: 'Medium'
                  },
                  {
                    title: 'Internal Link Building',
                    description: 'Creates strategic internal links between related pages to boost page authority',
                    frequency: 'Weekly',
                    impact: 'Medium'
                  },
                  {
                    title: 'Schema Markup Updates',
                    description: 'Adds and updates structured data for better search engine understanding',
                    frequency: 'As needed',
                    impact: 'Medium'
                  },
                  {
                    title: 'Sitemap Generation',
                    description: 'Automatically updates XML sitemaps when new content is published',
                    frequency: 'Real-time',
                    impact: 'Low'
                  }
                ].map((action, idx) => (
                  <div key={idx} className="border border-green-200 rounded-lg p-4 bg-green-50">
                    <h4 className="font-bold text-gray-900 mb-2">{action.title}</h4>
                    <p className="text-sm text-gray-700 mb-3">{action.description}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">
                        <Clock size={12} className="inline mr-1" />
                        {action.frequency}
                      </span>
                      <span className={`font-semibold ${
                        action.impact === 'High' ? 'text-green-600' :
                        action.impact === 'Medium' ? 'text-blue-600' : 'text-gray-600'
                      }`}>
                        {action.impact} Impact
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Semi-Automation */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-yellow-500 text-white px-4 py-2 rounded-lg font-bold">
                  SEMI-AUTOMATED - Draft Created, Human Approval Required
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    title: 'Review Responses',
                    description: 'AI drafts personalized responses to customer reviews that maintain brand voice',
                    reason: 'Customer-facing, requires personal touch',
                    approval: 'Before posting'
                  },
                  {
                    title: 'GMB Posts',
                    description: 'Creates Google Business Profile posts for offers, updates, and announcements',
                    reason: 'Public-facing content, promotional messaging',
                    approval: 'Before publishing'
                  },
                  {
                    title: 'Email Campaigns',
                    description: 'Generates marketing email content based on seasonality and promotions',
                    reason: 'Direct customer communication',
                    approval: 'Before sending'
                  },
                  {
                    title: 'Social Media Posts',
                    description: 'Creates social media content for Facebook, Instagram, and Twitter',
                    reason: 'Brand representation, public visibility',
                    approval: 'Before posting'
                  }
                ].map((action, idx) => (
                  <div key={idx} className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                    <h4 className="font-bold text-gray-900 mb-2">{action.title}</h4>
                    <p className="text-sm text-gray-700 mb-2">{action.description}</p>
                    <div className="text-xs text-gray-600 mb-1">
                      <strong>Why approval needed:</strong> {action.reason}
                    </div>
                    <div className="text-xs">
                      <span className="bg-yellow-600 text-white px-2 py-1 rounded">
                        Review {action.approval}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Monitoring Only */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold">
                  MONITORING ONLY - AI Alerts You, Human Takes Action
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    title: 'Negative Reviews',
                    description: 'AI detects negative reviews and drafts response, but human reviews before posting',
                    alert: 'Immediate email/SMS'
                  },
                  {
                    title: 'Major Ranking Drops',
                    description: 'AI identifies significant ranking losses and recommends recovery actions',
                    alert: 'Critical alert'
                  },
                  {
                    title: 'Website Issues',
                    description: 'AI detects technical problems like broken pages, slow loading, or downtime',
                    alert: 'Immediate notification'
                  },
                  {
                    title: 'Competitor Activity',
                    description: 'AI notices major competitor changes (new content, ranking gains, review spikes)',
                    alert: 'Daily digest'
                  }
                ].map((action, idx) => (
                  <div key={idx} className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                    <h4 className="font-bold text-gray-900 mb-2">{action.title}</h4>
                    <p className="text-sm text-gray-700 mb-2">{action.description}</p>
                    <div className="text-xs">
                      <span className="bg-blue-600 text-white px-2 py-1 rounded">
                        {action.alert}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule & Status Section */}
      {activeSection === 'schedule' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Automation Schedule</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-gray-900">AI Marketing Agent</h3>
                  <span className="bg-purple-100 text-purple-800 text-xs font-bold px-3 py-1 rounded-full">
                    Daily at 2:00 AM
                  </span>
                </div>
                <p className="text-sm text-gray-600">Full workflow execution (Intelligence + Analysis + Actions + Reports)</p>
              </div>

              <div className="border-l-4 border-blue-500 pl-4 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-gray-900">DataForSEO Keyword Collection</h3>
                  <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">
                    Daily at 2:00 AM
                  </span>
                </div>
                <p className="text-sm text-gray-600">Collects keyword metrics, suggestions, and PAA questions</p>
              </div>

              <div className="border-l-4 border-green-500 pl-4 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-gray-900">SERP Rank Tracking</h3>
                  <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full">
                    Daily at 3:00 AM
                  </span>
                </div>
                <p className="text-sm text-gray-600">Tracks search rankings, local pack positions, and visibility scores</p>
              </div>

              <div className="border-l-4 border-orange-500 pl-4 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-gray-900">Weekly Report Generation</h3>
                  <span className="bg-orange-100 text-orange-800 text-xs font-bold px-3 py-1 rounded-full">
                    Mondays at 6:00 AM
                  </span>
                </div>
                <p className="text-sm text-gray-600">Comprehensive weekly performance report sent to stakeholders</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="text-green-600" size={24} />
              <h3 className="text-lg font-bold text-green-900">System Status: Active</h3>
            </div>
            <p className="text-green-800 mb-4">
              All automation systems are running normally. Last successful execution: Today at 2:15 AM
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded p-3 border border-green-200">
                <div className="text-2xl font-bold text-green-600">100%</div>
                <div className="text-xs text-gray-600">Success Rate (30d)</div>
              </div>
              <div className="bg-white rounded p-3 border border-green-200">
                <div className="text-2xl font-bold text-green-600">156</div>
                <div className="text-xs text-gray-600">Actions Taken (7d)</div>
              </div>
              <div className="bg-white rounded p-3 border border-green-200">
                <div className="text-2xl font-bold text-green-600">43</div>
                <div className="text-xs text-gray-600">Blog Posts Created</div>
              </div>
              <div className="bg-white rounded p-3 border border-green-200">
                <div className="text-2xl font-bold text-green-600">2.3h</div>
                <div className="text-xs text-gray-600">Avg Execution Time</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
